import React, { useState, useEffect } from 'react';
import useMeta from '../hooks/useMeta';

export default function Galleri() {
  useMeta(
    'Galleri - Danry Diamant Boring ApS',
    'Se billeder fra udførte diamantboringsopgaver, ventilationsboringer og specialprojekter.'
  );

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/gallery');
        if (!response.ok) {
          throw new Error('Kunne ikke hente galleribilleder');
        }
        const data = await response.json();
        setItems(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, []);

  const categories = [
    { slug: 'all', label: 'Alle billeder' },
    { slug: 'diamantboring', label: 'Diamantboring' },
    { slug: 'ventilationsboring', label: 'Ventilationsboringer' },
    { slug: 'specialopgaver', label: 'Specialopgaver' },
  ];

  // Filter items based on active category
  const filteredItems = activeFilter === 'all'
    ? items
    : items.filter(item => {
        const catTerms = item.terms?.category || [];
        return catTerms.includes(activeFilter);
      });

  // Lightbox navigation
  const openLightbox = (index) => {
    setLightboxIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
    document.body.style.overflow = '';
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setLightboxIndex((prev) => (prev + 1) % filteredItems.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setLightboxIndex((prev) => (prev - 1 + filteredItems.length) % filteredItems.length);
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage(e);
      if (e.key === 'ArrowLeft') prevImage(e);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, filteredItems]);

  return (
    <div className="pt-[90px] lg:pt-[120px] min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-neutral-50 py-16 border-b border-neutral-200 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center max-w-3xl mx-auto flex flex-col gap-4">
          <h1 className="text-4xl font-extrabold text-neutral-900 tracking-tight uppercase">
            Projektgalleri
          </h1>
          <p className="text-base text-neutral-700 leading-relaxed">
            Se et udvalg af vores udførte opgaver indenfor diamantboring, ventilationsboringer og specialprojekter. Vi opdaterer løbende galleriet med nye billeder.
          </p>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-12 max-w-7xl mx-auto px-6">
        {/* Filter Controls */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {categories.map((cat) => (
            <button
              key={cat.slug}
              onClick={() => setActiveFilter(cat.slug)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer ${
                activeFilter === cat.slug
                  ? 'bg-brand-orange text-white shadow-md shadow-brand-orange/20'
                  : 'bg-white text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50 border border-neutral-200'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-12 h-12 border-4 border-brand-orange/20 border-t-brand-orange rounded-full animate-spin" />
            <p className="text-sm text-neutral-600">Henter galleribilleder fra CMS...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-500/10 text-red-500 text-xl mb-4">
              <i className="fa-solid fa-triangle-exclamation" />
            </div>
            <h3 className="text-lg font-bold text-neutral-900 uppercase">Der opstod en fejl</h3>
            <p className="text-sm text-neutral-600 mt-1">{error}</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredItems.length === 0 && (
          <div className="text-center py-20 text-neutral-600">
            <i className="fa-regular fa-image text-4xl mb-4 text-neutral-400" />
            <p>Ingen billeder fundet i denne kategori.</p>
          </div>
        )}

        {/* Image Grid */}
        {!loading && !error && filteredItems.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredItems.map((item, index) => {
              // Cloudflare resized image URL for grid item
              const imageUrl = item.image?.[0] || '';
              const gridImageUrl = imageUrl
                ? `https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${imageUrl.replace('https://assets.auxodev.dk/', '')}`
                : '';

              return (
                <div
                  key={item.id}
                  onClick={() => openLightbox(index)}
                  className="group relative rounded-none overflow-hidden border border-neutral-200 bg-neutral-50 aspect-4/3 cursor-pointer shadow-sm hover:shadow-xl hover:border-brand-orange/30 transition-all duration-300"
                >
                  <img
                    src={gridImageUrl}
                    alt={item.title?.da || 'Galleribillede'}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                    <h3 className="text-lg font-bold text-white leading-tight uppercase">
                      {item.title?.da}
                    </h3>
                    {item.description?.da && (
                      <p className="text-xs text-neutral-300 mt-1.5 line-clamp-2">
                        {item.description?.da}
                      </p>
                    )}
                    <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-orange">
                      <i className="fa-solid fa-magnifying-glass-plus" />
                      Vis i stor størrelse
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && filteredItems[lightboxIndex] && (
        <div
          onClick={closeLightbox}
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex flex-col items-center justify-center p-4 animate-fadeIn select-none"
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-neutral-900/80 hover:bg-neutral-800 text-white flex items-center justify-center text-xl transition-colors cursor-pointer border border-neutral-800"
            aria-label="Luk galleri"
          >
            <i className="fa-solid fa-xmark" />
          </button>

          {/* Navigation Buttons */}
          <button
            onClick={prevImage}
            className="absolute left-6 w-12 h-12 rounded-full bg-neutral-900/80 hover:bg-neutral-800 text-white flex items-center justify-center text-xl transition-colors cursor-pointer border border-neutral-800"
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left" />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-6 w-12 h-12 rounded-full bg-neutral-900/80 hover:bg-neutral-800 text-white flex items-center justify-center text-xl transition-colors cursor-pointer border border-neutral-800"
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right" />
          </button>

          {/* Lightbox Content */}
          <div
            onClick={(e) => e.stopPropagation()}
            className="max-w-5xl w-full flex flex-col gap-4 items-center"
          >
            <div className="relative rounded-none overflow-hidden border border-neutral-800 bg-neutral-950 max-h-[70vh] flex items-center justify-center shadow-2xl">
              {/* Cloudflare resized image URL for lightbox */}
              <img
                src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/${filteredItems[lightboxIndex].image?.[0].replace('https://assets.auxodev.dk/', '')}`}
                alt={filteredItems[lightboxIndex].title?.da || 'Galleribillede i stor størrelse'}
                className="max-h-[70vh] object-contain"
              />
            </div>
            
            {/* Text & Counter */}
            <div className="text-center max-w-2xl px-4 flex flex-col gap-1.5">
              <h3 className="text-xl font-bold text-white uppercase">
                {filteredItems[lightboxIndex].title?.da}
              </h3>
              {filteredItems[lightboxIndex].description?.da && (
                <p className="text-sm text-neutral-400">
                  {filteredItems[lightboxIndex].description?.da}
                </p>
              )}
              <div className="text-xs text-neutral-500 mt-1">
                Billede {lightboxIndex + 1} af {filteredItems.length}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
